import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../../store/actions";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Card,
  CardMedia,
  InputAdornment,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  Checkbox,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { styled } from "@mui/system";
import illustration from "../../assets/images/loginpage.png";

// Styled components
const StyledContainer = styled(Container)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  minWidth: "100vw",
  background: "linear-gradient(to bottom right, #EDE9FE, #2c7a7c)",
  padding: 0,
  margin: 0,
  maxWidth: "100% !important",
  overflow: "hidden",
});

const StyledCard = styled(Card)({
  display: "flex",
  width: "100%",
  maxWidth: "1000px",
  borderRadius: "1.5rem",
  boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
  overflow: "hidden",
});

const ImageSection = styled(Box)({
  width: "50%",
  backgroundColor: "#9eb4b7",
  padding: "2.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const FormSection = styled(Box)({
  width: "50%",
  padding: "3rem",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});

const StyledDialog = styled(Dialog)({
  "& .MuiDialog-paper": {
    borderRadius: "1rem",
    backgroundColor: "#fcfefd",
    color: "#3c8283",
    padding: "1rem",
  },
  "& .MuiDialogTitle-root": {
    fontWeight: "bold",
  },
});

// Login Component
const Login = () => {
  document.title = "Login";

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMsg, setDialogMsg] = useState("");

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      UserName: "",
      password: "",
    },
    validationSchema: Yup.object({
      UserName: Yup.string().required("Please Enter UserName"),
      password: Yup.string().required("Please Enter Your Password"),
    }),
    onSubmit: (values) => {
      setLoading(true);
      dispatch(loginUser(values, navigate))
        .then(() => {
          setLoading(false);
          // Success: Navigate to another page or handle success state
        })
        .catch((error) => {
          setLoading(false);
          console.log("i m inside");
          setDialogMsg(error.message || "Login Failed. Please try again.");
          setDialogOpen(true);

        });
    },
  });

  const { error } = useSelector((state) => ({
    error: state.login.error,
  }));

  useEffect(() => {
    document.body.className = "bg-pattern";
    return () => {
      document.body.className = "";
    };
  }, []);

  return (
    <StyledContainer>
      <StyledCard>
      <ImageSection>
  <CardMedia
    component="img"
    image={illustration}
    alt="Illustration"
    sx={{
      borderRadius: "1.5rem",
      boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
      maxWidth: "100%",
      maxHeight: "100%",
      objectFit: "contain",
    }}
  />
</ImageSection>

<FormSection>
  <Typography variant="h4" sx={{ mt: 2, mb: 1, fontWeight: 700, color: "#1F2937" }}>
    Welcome Back, Doctor
  </Typography>
  <Typography sx={{ color: "#6B7280", fontSize: "0.95rem" }}>
    Please sign in to continue
  </Typography>

  <form onSubmit={(e) => { e.preventDefault(); validation.handleSubmit(); }}>
    <TextField
      name="UserName"
      placeholder="Enter email"
      variant="outlined"
      fullWidth
      sx={{ mt: 4, bgcolor: "#F9FAFB", borderRadius: "0.75rem" }}
      onChange={validation.handleChange}
      onBlur={validation.handleBlur}
      value={validation.values.UserName}
      error={validation.touched.UserName && Boolean(validation.errors.UserName)}
      helperText={validation.touched.UserName && validation.errors.UserName}
    />

    <TextField
      name="password"
      type={showPassword ? "text" : "password"}
      placeholder="Password"
      variant="outlined"
      fullWidth
      sx={{ mt: 2, bgcolor: "#F9FAFB", borderRadius: "0.75rem" }}
      onChange={validation.handleChange}
      onBlur={validation.handleBlur}
      value={validation.values.password}
      error={validation.touched.password && Boolean(validation.errors.password)}
      helperText={validation.touched.password && validation.errors.password}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={toggleShowPassword} edge="end">
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />

    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Checkbox
          checked={validation.values.rememberMe || false}
          onChange={(e) => validation.setFieldValue("rememberMe", e.target.checked)}
          sx={{ color: "#6B7280", "&.Mui-checked": { color: "#3c8283" } }}
        />
        <Typography sx={{ color: "#6B7280" }}>Remember Me</Typography>
      </Box>
      <Typography>
        {/* <Link to="/forgot-password" style={{ color: "#3c8283", textDecoration: "none", mt: 2  }}>
          Forgot Password?
        </Link> */}
      </Typography>
    </Box>

    <Button
      type="submit"
      variant="contained"
      fullWidth
      disabled={loading}
      sx={{
        mt: 3,
        py: 1.5,
        borderRadius: "0.75rem",
        bgcolor: "#3c8283",
        color: "#fcfefd",
        fontWeight: 600,
        "&:hover": { bgcolor: "#336f70" },
      }}
      startIcon={loading && <CircularProgress size={20} color="inherit" />}
    >
      {loading ? "Signing In..." : "Sign In"}
    </Button>

    <Typography sx={{ mt: 2, textAlign: "center" }}>
      Need help? Contact <a href="mailto:support@endomind.com">support@endomind.com</a>
    </Typography>
  </form>
</FormSection>
      </StyledCard>

      <StyledDialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Login Failed</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogMsg}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              color: "#fcfefd",
              bgcolor: "#3c8283",
              "&:hover": {
                bgcolor: "#2c6768",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </StyledDialog>
    </StyledContainer>
  );
};

Login.propTypes = {
  // No longer need this prop as we're using React Router v6 hooks
  // history: PropTypes.object,
};

export default Login;
